import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { ArrowRight, Package, Trash2, Pencil, Layers } from 'lucide-react';
const CATEGORY_OPTIONS = ['Food Cost', 'Packaging', 'Supplies', 'Non-Business'];
const UNIT_OPTIONS = ['oz', 'lb', 'g', 'kg', 'each', 'cup', 'tbsp', 'tsp', 'ml', 'l'];

interface COGSVendorTrainingStep6Props {
  onNext: () => void;
  onPrevious: () => void;
  selectedVendors: string[];
  onEditMapping?: (vendorId: string, clusterId?: string) => void;
}

interface TrainedMapping {
  id: string;
  vendor_id: string;
  vendor_name: string;
  receipt_item_name: string;
  inventory_item_id: string;
  inventory_item_name: string;
  category: string;
  purchase_qty: number;
  purchase_unit: string;
  purchase_price: number;
  recipe_unit: string;
  cost_per_recipe_unit: number;
  invoice_date?: string;
}

interface IngredientWithMultipleMappings {
  ingredientId: string;
  ingredientName: string;
  mappings: Array<{
    mappingId: string;
    vendorProductDescription: string;
    vendorId: string;
    costPerRecipeUnit: number;
    recipeUnit: string;
  }>;
}

export const COGSVendorTrainingStep6 = ({ onNext, onPrevious, selectedVendors, onEditMapping }: COGSVendorTrainingStep6Props) => {
  const { toast } = useToast();
  const [mappings, setMappings] = useState<TrainedMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState<any[]>([]);
  const [editingMapping, setEditingMapping] = useState<TrainedMapping | null>(null);
  const [editCategory, setEditCategory] = useState('');
  const [editPurchaseQty, setEditPurchaseQty] = useState('');
  const [editRecipeUnit, setEditRecipeUnit] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [ingredientsWithMultipleMappings, setIngredientsWithMultipleMappings] = useState<IngredientWithMultipleMappings[]>([]);
  const [allIngredients, setAllIngredients] = useState<Array<{ id: string; name: string }>>([]);
  const [editInventoryItemId, setEditInventoryItemId] = useState('');
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [confirmedMultiMappingIngredientIds, setConfirmedMultiMappingIngredientIds] = useState<string[]>([]);

  useEffect(() => {
    loadMappings();
  }, [selectedVendors]);

  const loadMappings = async () => {
    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      // Load vendors and ingredients in parallel
      const [{ data: vendorsData }, { data: ingredientsData }] = await Promise.all([
        supabase.from('vendors').select('id, name').in('id', selectedVendors),
        supabase.from('ingredients').select('id, name').eq('company_id', companyId).order('name'),
      ]);
      setAllIngredients((ingredientsData || []).map((i: any) => ({ id: i.id, name: i.name })));
      // (vendorsData kept below for backward compat)

      setVendors(vendorsData || []);

      // Load trained mappings
      const { data: mappingsData, error } = await supabase
        .from('vendor_ingredient_mappings')
        .select(`
          id,
          vendor_id,
          vendor_product_description,
          category,
          purchase_qty,
          purchase_unit,
          price_per_unit,
          recipe_unit,
          cost_per_recipe_unit,
          ingredients:ingredient_id (
            id,
            name
          )
        `)
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors)
        .not('ingredient_id', 'is', null);

      if (error) throw error;

      const vendorMap = new Map((vendorsData || []).map(v => [v.id, v.name]));

      const trainedMappings: TrainedMapping[] = (mappingsData || []).map((m: any) => ({
        id: m.id,
        vendor_id: m.vendor_id,
        vendor_name: vendorMap.get(m.vendor_id) || 'Unknown',
        receipt_item_name: m.vendor_product_description || '',
        inventory_item_id: m.ingredients?.id || '',
        inventory_item_name: m.ingredients?.name || 'Unknown',
        category: m.category || 'Other',
        purchase_qty: m.purchase_qty || 1,
        purchase_unit: m.purchase_unit || 'each',
        purchase_price: m.price_per_unit || 0,
        recipe_unit: m.recipe_unit || m.purchase_unit || '',
        cost_per_recipe_unit: m.cost_per_recipe_unit || 0,
      }));

      setMappings(trainedMappings);

      // Detect ingredients with multiple receipt item mappings
      const ingredientMappingsMap = new Map<string, IngredientWithMultipleMappings>();
      trainedMappings.forEach((m) => {
        if (m.inventory_item_id && m.inventory_item_name) {
          if (!ingredientMappingsMap.has(m.inventory_item_id)) {
            ingredientMappingsMap.set(m.inventory_item_id, {
              ingredientId: m.inventory_item_id,
              ingredientName: m.inventory_item_name,
              mappings: [],
            });
          }
          ingredientMappingsMap.get(m.inventory_item_id)!.mappings.push({
            mappingId: m.id,
            vendorProductDescription: m.receipt_item_name || 'Unknown',
            vendorId: m.vendor_id,
            costPerRecipeUnit: m.cost_per_recipe_unit || 0,
            recipeUnit: m.recipe_unit || '',
          });
        }
      });
      const multiMapped = Array.from(ingredientMappingsMap.values()).filter(ing => ing.mappings.length > 1);
      setIngredientsWithMultipleMappings(multiMapped);

    } catch (error: any) {
      console.error('Error loading mappings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load trained mappings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { error } = await supabase
        .from('vendor_ingredient_mappings')
        .delete()
        .eq('id', mappingId)
        .eq('company_id', companyId);

      if (error) throw error;

      setMappings(prev => prev.filter(m => m.id !== mappingId));
      toast({
        title: 'Deleted',
        description: 'Ingredient group mapping removed.',
      });
    } catch (error: any) {
      console.error('Error deleting mapping:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete mapping',
        variant: 'destructive',
      });
    }
  };

  const handleEditMapping = (mapping: TrainedMapping) => {
    setEditingMapping(mapping);
    setEditCategory(mapping.category || '');
    setEditPurchaseQty(String(mapping.purchase_qty || ''));
    setEditRecipeUnit(mapping.recipe_unit || '');
    setEditInventoryItemId(mapping.inventory_item_id || '');
    setIngredientSearch('');
  };

  const handleSaveEdit = async () => {
    if (!editingMapping) return;
    setSavingEdit(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const selectedIngredient = allIngredients.find(i => i.id === editInventoryItemId);

      const { error } = await supabase
        .from('vendor_ingredient_mappings')
        .update({
          category: editCategory,
          purchase_qty: parseFloat(editPurchaseQty) || editingMapping.purchase_qty,
          recipe_unit: editRecipeUnit,
          ingredient_id: editInventoryItemId || editingMapping.inventory_item_id,
        })
        .eq('id', editingMapping.id)
        .eq('company_id', companyId);

      if (error) throw error;

      setMappings(prev => prev.map(m =>
        m.id === editingMapping.id
          ? {
              ...m,
              category: editCategory,
              purchase_qty: parseFloat(editPurchaseQty) || m.purchase_qty,
              recipe_unit: editRecipeUnit,
              inventory_item_id: editInventoryItemId || m.inventory_item_id,
              inventory_item_name: selectedIngredient?.name || m.inventory_item_name,
            }
          : m
      ));

      toast({ title: 'Updated', description: 'Mapping updated successfully.' });
      setEditingMapping(null);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to update mapping', variant: 'destructive' });
    } finally {
      setSavingEdit(false);
    }
  };

  const visibleMultiMappings = ingredientsWithMultipleMappings.filter(
    (ing) => !confirmedMultiMappingIngredientIds.includes(ing.ingredientId)
  );

  const handleConfirmMultiMapping = (ingredient: IngredientWithMultipleMappings) => {
    setConfirmedMultiMappingIngredientIds((prev) =>
      prev.includes(ingredient.ingredientId) ? prev : [...prev, ingredient.ingredientId]
    );
    toast({
      title: 'Grouping confirmed',
      description: `Multiple receipt items are confirmed under "${ingredient.ingredientName}". You can still edit details in the table below if needed.`,
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group mappings by category
  const mappingsByCategory = mappings.reduce((acc, mapping) => {
    const cat = mapping.category || 'Uncategorized';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(mapping);
    return acc;
  }, {} as Record<string, TrainedMapping[]>);

  // Sort categories: known categories first, then alphabetically
  const CATEGORY_ORDER = ['Food Cost', 'Packaging', 'Supplies', 'Non-Business', 'Uncategorized'];
  const sortedCategories = Object.keys(mappingsByCategory).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Review Trained Ingredient Groups</h3>
        <p className="text-sm text-muted-foreground">
          Review all the ingredient groups you've trained. You can edit or delete any group if needed.
        </p>
      </div>

      {/* Duplicate Mappings Warning */}
      {visibleMultiMappings.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <Layers className="h-4 w-4 text-amber-600" />
          <AlertDescription>
            <div className="space-y-3">
              <p className="font-semibold text-amber-800">
                Multiple Receipt Items in Same Ingredient Group ({visibleMultiMappings.length})
              </p>
              <p className="text-sm text-amber-700">
                The following inventory items have multiple vendor receipt descriptions mapped to them.
                This is normal for the same ingredient with different names on invoices.
                Review these and confirm that the groupings are correct. You can still edit details in the table below if needed — the system uses the most recent price for cost calculations.
              </p>
              <div className="space-y-2 mt-2">
                {visibleMultiMappings.map((ing) => (
                  <div key={ing.ingredientId} className="bg-white/60 rounded-lg p-3 border border-amber-200">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        <span className="font-medium text-amber-900">{ing.ingredientName}</span>
                        <Badge variant="outline" className="text-amber-700 border-amber-300">
                          {ing.mappings.length} receipt items
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => handleConfirmMultiMapping(ing)}
                      >
                        Confirm Grouping
                      </Button>
                    </div>
                    <div className="grid gap-1 text-sm">
                      {ing.mappings.map((m, idx) => {
                        const mapping = mappings.find(mp => mp.id === m.mappingId);
                        return (
                          <div key={idx} className="flex items-center justify-between text-amber-700 pl-6">
                            <span className="font-mono text-xs">{m.vendorProductDescription}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">
                                ${m.costPerRecipeUnit.toFixed(2)}/{m.recipeUnit}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {mappings.length === 0 ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <Package className="h-16 w-16 text-amber-600 mx-auto" />
              <div>
                <h4 className="font-semibold mb-2 text-lg">No Ingredient Groups Yet</h4>
                <p className="text-muted-foreground">Please complete the training step first.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedCategories.map((category) => {
            const categoryMappings = mappingsByCategory[category];
            return (
              <Card key={category}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Badge variant="secondary">{category}</Badge>
                    <span className="text-sm font-normal text-muted-foreground">
                      {categoryMappings.length} item{categoryMappings.length !== 1 ? 's' : ''}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Receipt Item</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Inventory Item</TableHead>
                        <TableHead>Qty / Unit</TableHead>
                        <TableHead>Cost/Recipe Unit</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryMappings.map((mapping) => (
                        <TableRow key={mapping.id}>
                          <TableCell className="font-medium max-w-[200px] truncate" title={mapping.receipt_item_name}>
                            {mapping.receipt_item_name}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">{mapping.vendor_name}</TableCell>
                          <TableCell className="max-w-[160px] truncate" title={mapping.inventory_item_name}>
                            {mapping.inventory_item_name}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {mapping.purchase_qty} {mapping.purchase_unit}
                          </TableCell>
                          <TableCell className="font-mono font-semibold whitespace-nowrap">
                            ${mapping.cost_per_recipe_unit.toFixed(2)}/{mapping.recipe_unit}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditMapping(mapping)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMapping(mapping.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button onClick={onNext}>
          Continue to Complete
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {editingMapping && (
        <Dialog open={!!editingMapping} onOpenChange={(open) => { if (!open) setEditingMapping(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Ingredient Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm font-medium mb-1">Receipt Item</p>
                <p className="text-sm text-muted-foreground font-mono bg-muted p-2 rounded">
                  {editingMapping.receipt_item_name || 'Unknown'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Inventory Item Name</Label>
                <div className="space-y-1">
                  <Input
                    placeholder="Search inventory items..."
                    value={ingredientSearch}
                    onChange={(e) => setIngredientSearch(e.target.value)}
                  />
                  <Select value={editInventoryItemId} onValueChange={setEditInventoryItemId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select inventory item">
                        {editInventoryItemId
                          ? (allIngredients.find(i => i.id === editInventoryItemId)?.name || editingMapping.inventory_item_name)
                          : editingMapping.inventory_item_name}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {allIngredients
                        .filter(i => !ingredientSearch || i.name.toLowerCase().includes(ingredientSearch.toLowerCase()))
                        .map(i => (
                          <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={editCategory} onValueChange={setEditCategory}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Purchase Qty</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editPurchaseQty}
                  onChange={(e) => setEditPurchaseQty(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Recipe Unit</Label>
                <Select value={editRecipeUnit} onValueChange={setEditRecipeUnit}>
                  <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingMapping(null)}>Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={savingEdit || !editCategory || !editRecipeUnit}>
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
